<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto px-4 py-8 max-w-7xl">
      <!-- Quick Stats -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">Total Tickets</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ tickets.length }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">Unassigned</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ unassignedCount }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">In Progress</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ inProgressCount }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">Completed</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ completedCount }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters Bar -->
      <div
        class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-6"
      >
        <div class="flex flex-col lg:flex-row gap-4">
          <!-- Search -->
          <div class="flex-1 relative">
            <div
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              v-model="filters.search"
              type="text"
              placeholder="Search tickets by number or client..."
              class="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-50 focus:border-transparent sm:text-sm"
            />
          </div>

          <!-- Status Filter -->
          <div class="w-full lg:w-48">
            <select
              v-model="filters.status"
              class="block w-full pl-3 pr-10 py-2 text-base bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-50 focus:border-transparent sm:text-sm rounded-lg"
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Verified">Verified</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>

          <!-- Assignment Filter (for supervisors) -->
          <div v-if="userRole === 'supervisor'" class="w-full lg:w-48">
            <select
              v-model="filters.assignedTo"
              class="block w-full pl-3 pr-10 py-2 text-base bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-50 focus:border-transparent sm:text-sm rounded-lg"
            >
              <option value="">All Assignments</option>
              <option value="unassigned">Unassigned</option>
              <option value="me">Assigned to Me</option>
              <option
                v-for="officer in verificationOfficers"
                :key="officer.id"
                :value="officer.id"
              >
                {{ officer.name }}
              </option>
            </select>
          </div>

          <!-- Priority Filter -->
          <div class="w-full lg:w-40">
            <select
              v-model="filters.priority"
              class="block w-full pl-3 pr-10 py-2 text-base bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-50 focus:border-transparent sm:text-sm rounded-lg"
            >
              <option value="">All Priorities</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>

          <!-- Reset Button -->
          <button
            @click="resetFilters"
            class="px-4 py-2 text-sm font-medium border border-indigo-800 text-indigo-800 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center"
          >
            <svg
              class="w-4 h-4 mr-2 text-indigo-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            RESET
          </button>
        </div>
      </div>

      <!-- Tickets Table -->
      <div
        class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
      >
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Ticket #
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Client
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Verification
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Assigned To
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Created
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="tickets.length === 0">
                <td colspan="7" class="px-6 py-12 text-center">
                  <div class="flex flex-col items-center">
                    <svg
                      class="w-12 h-12 text-gray-300 mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                      />
                    </svg>
                    <p class="text-gray-500 text-lg mb-1">No tickets found</p>
                    <p class="text-gray-400 text-sm">
                      Try adjusting your search or filters
                    </p>
                  </div>
                </td>
              </tr>
              <tr
                v-for="ticket in paginatedTickets"
                :key="ticket.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="text-sm font-medium text-gray-900">{{
                    ticket.ticket_number
                  }}</span>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm font-medium text-gray-900">
                    {{ ticket.client?.full_name }}
                  </div>
                  <div class="text-sm text-gray-500">
                    {{ formatKenyanPhone(ticket.client?.phone) }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <StatusBadge :status="ticket.decision" />
                </td>
                <td class="px-6 py-4">
                  <div class="flex items-center gap-1 flex-wrap max-w-[220px]">
                    <span
                      v-for="stage in verificationStages"
                      :key="stage.key"
                      class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                      :class="getVerificationBadgeClass(ticket, stage.key)"
                      :title="`${stage.label}: ${getVerificationLabel(ticket, stage.key)}`"
                    >
                      {{ stage.label }}
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div v-if="ticket.user_id" class="flex items-center">
                    <span class="text-sm text-gray-900">{{
                      ticket.priority
                    }}</span>
                  </div>
                  <span v-else class="text-sm text-gray-400 italic"
                    >Unassigned</span
                  >
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>
                   <div class="text-sm font-medium text-gray-900">
  {{ formatDate(ticket.created_at) }}
</div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex items-center space-x-2">

                    <!-- Start progress button (for assigned officers) -->
                    <button
                      @click="openModal(ticket)"
                      class="border flex items-center rounded-lg border-indigo-800 text-indigo-800 hover:text-indigo-700 p-2 rounded hover:bg-blue-50"
                      title="Start progress"
                    >
                      <svg
                        class="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div>Select</div>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div
          class="px-6 py-4 border-t border-gray-100 flex items-center justify-between"
        >
          <div class="flex items-center space-x-2">
            <span class="text-sm text-gray-500">Items per page:</span>
            <select
              v-model="itemsPerPage"
              class="border border-gray-200 rounded-lg text-sm py-1 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>

          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-500">
              {{ paginationStart }}-{{ paginationEnd }} of
              {{ tickets.length }}
            </span>
            <div class="flex items-center space-x-2">
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  class="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  class="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Select Ticket Modal -->
      <div class="">
        <!-- Modern Modal Backdrop -->
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="isModalOpen"
            class="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40"
            @click="closeModal"
          />
        </Transition>

        <!-- Modern Modal Panel -->
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="isModalOpen"
            class="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              class="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto border border-slate-200 overflow-hidden"
            >
              <!-- Modal header with accent bar -->
              <div class="h-2 bg-primary-50" />

              <div class="p-6">
                <!-- Icon and title -->
                <div class="flex items-start gap-4">
                  <div class="shrink-0 bg-slate-100 p-3 rounded-xl">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6 text-slate-700"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div class="flex-1">
                    <h3 class="text-xl font-semibold text-slate-800">
                      Start working?
                    </h3>
                    <p class="text-slate-500 mt-1 text-sm leading-relaxed">
                      You're about to begin working on this ticket. This will
                      change its status.
                    </p>
                  </div>
                </div>

                <!-- Ticket preview card -->
                <div
                  class="mt-5 bg-slate-50 rounded-xl p-4 border border-slate-200/70"
                >
                  <div class="flex items-start gap-3">
                    <div class="shrink-0">
                      <div
                        class="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center text-slate-700 font-mono text-xs"
                      >
                        {{ selectedTicket?.ticket_number?.slice(-3) }}
                      </div>
                    </div>
                    <div>
                      <span class="text-xs font-mono text-slate-400">{{
                        selectedTicket?.cient_name
                      }}</span>
                      <p class="text-sm font-medium text-slate-700 mt-0.5">
                        {{ selectedTicket?.ticket_number }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Actions -->
                <div class="mt-6 flex gap-3 justify-end">
                  <button
                    @click="closeModal"
                    class="px-5 py-2.5 text-sm font-medium text-red-400 hover:text-red-400 border border-red-400 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    Cancel
                  </button>
                  <button
                    @click="confirmStart"
                    class="px-5 py-2.5 text-sm font-medium text-white bg-indigo-800 hover:bg-indigo-700 rounded-lg shadow-sm transition-all duration-150 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                  >
                    Confirm & start
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>


      <!-- Success Notification -->
      <div
        v-if="showNotification"
        class="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 animate-slide-up z-50"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>{{ notificationMessage }}</span>
      </div>
    </div>

    <!-- Floating Action Button -->
    <button
      @click="getTickets"
      class="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-800 text-white shadow-lg transition-all duration-300 hover:bg-indigo-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      aria-label="Refresh page"
    >
      <!-- Refresh icon (SVG) -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        :class="{ 'animate-spin': isRefreshing }"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    </button>

    <div
      v-if="isRefreshing"
      class="fixed inset-0 ml-64 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <div class="relative">
        <!-- Outer ring -->
        <div class="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
        <!-- Inner spinning gradient ring -->
        <div
          class="w-16 h-16 border-4 border-transparent rounded-full absolute top-0 left-0 border-t-green-800 border-r-green-950 animate-spin"
        ></div>
        <!-- Center dot -->
        <div
          class="w-3 h-3 bg-green-700 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import StatusBadge from "@/views/components/StatusBadge.vue";
import { useAuthStore } from "@/stores/authStore";
import api from "@/utils/api";
import { useNotifications } from "@/composables/useNotifications";

const authStore = useAuthStore();

// Notification system
const { showError, formatValidationErrors } = useNotifications();

const router = useRouter();

// User state
const isRefreshing = ref(false);
const userRole = ref("Verification Officer"); // 'supervisor' or 'officer'
const currentUser = ref({});

const verificationOfficers = ref([]);

if (authStore.checkAuth()) {
  const user = authStore.user;
  currentUser.value = user;
  userRole.value = user.role;
} else {
  // Log out user
}

const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) return "N/A";

    return new Intl.DateTimeFormat("en-KE", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch (error) {
    console.log(error);
    return "N/A";
  }
};

// Verification stages shown per row
const verificationStages = [
  { key: "kyc_verifications", label: "KYC" },
  { key: "contact_verifications", label: "Contact" },
  { key: "sms_verifications", label: "SMS" },
  { key: "final_verifications", label: "Final" },
];

// Backend returns each verification relation ordered latest()->get(),
// so index 0 is always the most recent record for that stage
const getLatestVerification = (ticket, key) => {
  const records = ticket[key];
  return records && records.length ? records[0] : null;
};

const getVerificationLabel = (ticket, key) => {
  const latest = getLatestVerification(ticket, key);
  if (!latest) return "Not Started";
  return latest.decision === 1 ? "Approved" : "Rejected";
};

const getVerificationBadgeClass = (ticket, key) => {
  const latest = getLatestVerification(ticket, key);
  if (!latest) return "bg-gray-100 text-gray-500";
  return latest.decision === 1
    ? "bg-green-100 text-green-700"
    : "bg-red-100 text-red-700";
};

// Tickets data
const tickets = ref([]);

// Modal state
const isModalOpen = ref(false);
const selectedTicket = ref(null);

// Methods
const openModal = (ticket) => {
  selectedTicket.value = ticket;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  // Clear selected after animation
  setTimeout(() => {
    if (!isModalOpen.value) {
      selectedTicket.value = null;
    }
  }, 150);
};

const confirmStart = async () => {
  if (selectedTicket.value) {
    try {
      console.log("selectedTicket", selectedTicket.value);
      const response = await api.post("/api/v1/tickets/assign", {
        ticket_id: selectedTicket.value.id,
      });

      if (response.data.success) {
        // Ticket successfully assigned
        console.log(response.data.message);
        closeModal();
        // Update local state
        if(userRole.value === "Verification Officer"){
          router.push(`/admin/tickets/${selectedTicket.value.id}`);
        }else if(userRole.value === "SMS"){
          router.push(`/admin/sms/${selectedTicket.value.id}`);
        }
      }
    } catch (error) {
      if (error.response?.status === 409) {
        // Ticket assigned to someone else
        alert("This ticket is already assigned to another user");
      }
      const details = formatValidationErrors(error);
      showError("Error", "Failed to load tickets", details, 5000);
    }
  }
};

const getTickets = async () => {
  isRefreshing.value = true;
  try {
    const response = await api.get("/api/v2/tickets");
    tickets.value = response.data.data;
  } catch (error) {
    console.log(error);
    const details = formatValidationErrors(error);
    showError("Error", "Failed to load tickets", details, 5000);
  } finally {
    isRefreshing.value = false;
  }
};

onMounted(() => {
  getTickets();
});

// Filters
const filters = ref({
  search: "",
  status: "",
  assignedTo: "",
  priority: "",
});

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(10);

// Notification state
const showNotification = ref(false);
const notificationMessage = ref("");

// Computed properties
const unassignedCount = computed(() => {
  return tickets.value.filter((t) => !t.user_id).length;
});

const inProgressCount = computed(() => {
  return tickets.value.filter((t) => t.decision === "In Progress").length;
});

const completedCount = computed(() => {
  return tickets.value.filter(
    (t) => t.decision === "Verified" || t.decision === "Rejected"
  ).length;
});

// Filtered tickets
const filteredTickets = computed(() => {
  return tickets.value.filter((ticket) => {
    // Search filter
    const matchesSearch =
      !filters.value.search ||
      ticket.ticket_number
        .toLowerCase()
        .includes(filters.value.search.toLowerCase()) ||
      ticket.client_name
        .toLowerCase()
        .includes(filters.value.search.toLowerCase()) ||
      ticket.phone
        .toLowerCase()
        .includes(filters.value.search.toLowerCase());

    // Status filter
    const matchesStatus =
      !filters.value.status || ticket.decision === filters.value.status;

    // Priority filter
    const matchesPriority =
      !filters.value.priority || ticket.priority === filters.value.priority;

    // Assignment filter (for supervisors)
    let matchesAssignment = true;
   
    return (
      matchesSearch && matchesStatus && matchesPriority && matchesAssignment
    );
  });
});

// Paginated tickets
const paginatedTickets = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredTickets.value.slice(start, end);
});

// Pagination helpers
const totalPages = computed(() =>
  Math.ceil(filteredTickets.value.length / itemsPerPage.value)
);
const paginationStart = computed(
  () => (currentPage.value - 1) * itemsPerPage.value + 1
);
const paginationEnd = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, filteredTickets.value.length)
);

const formatKenyanPhone = (phoneNumber) => {
  if (!phoneNumber) return "N/A";

  try {
    // Convert to string and remove any non-digit characters
    const cleaned = String(phoneNumber).replace(/\D/g, "");

    // Check if it's a Kenyan number starting with 254
    if (cleaned.startsWith("254")) {
      // Remove '254' and ensure we have the remaining 9 digits
      const localNumber = cleaned.substring(3);

      // If we have exactly 9 digits after removing 254, format with leading 0
      if (localNumber.length === 9) {
        return "0" + localNumber;
      }
      // If we have 12 digits total (254 + 9 digits), take the last 9
      else if (cleaned.length === 12) {
        return "0" + cleaned.slice(-9);
      }
    }

    // Return original cleaned number if not in Kenyan format
    return cleaned;
  } catch (error) {
    console.log(error);
    return "N/A";
  }
};

// Actions
const resetFilters = () => {
  filters.value = {
    search: "",
    status: "",
    assignedTo: "",
    priority: "",
  };
  currentPage.value = 1;
};

// Watch for filter changes
watch(
  filters,
  () => {
    currentPage.value = 1;
  },
  { deep: true }
);
</script>

<style scoped>
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-up {
  animation: slideUp 0.3s ease-out;
}
</style>